import React from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Button from "app/popleads/components/ui-kit/Button";
import { canAskAssistant } from "./assistantAccess";
import { askAboutError, canAskAboutError, claimRecentFailure, errorContext } from "./assistantErrorCapture";

import styles from "./assistantErrorToast.module.scss";

/* The two ways the button can fail to open a conversation: the assistant is still on another one, or
   the window that answers is no longer there -- a toast or a card that outlived its page. */
export const ASSISTANT_BUSY = "The assistant is still answering another question. Try again in a moment.";
export const ASSISTANT_CLOSED =
    "The assistant cannot be reached from this page. Open the assistant and ask about the error there.";

/** What to tell the user when handing the error over did not open a conversation. */
export const notAsked = () => toast.info(canAskAboutError() ? ASSISTANT_BUSY : ASSISTANT_CLOSED);

/* Long enough to read the message and reach the button; the hover pause covers the rest. */
const AUTO_CLOSE_WITH_BUTTON_MS = 8000;

/**
 * An error toast with "Ask the assistant" under its message. Rendered inside the ToastContainer,
 * so it can read the signed-in user — the button is for holders of CRM_ASSISTANT only, and only
 * while the assistant's window is mounted to take it (the CRM's subheader, or the extension's panel).
 * Everyone else sees the toast exactly as it was.
 */
export const ErrorToast = ({ content, failure, closeToast }) => {
    const { user } = useSelector((state) => state.auth);
    const message = typeof content === "function" ? content({ closeToast }) : content;
    if (!canAskAssistant(user) || !canAskAboutError()) return <>{message}</>;

    const ask = () => {
        const shown = typeof content === "string" ? content : undefined;
        if (!askAboutError(errorContext({ ...failure, shown }))) {
            notAsked();
            return;
        }
        if (closeToast) closeToast();
    };

    return (
        <div className={styles.toast}>
            <div>{message}</div>
            <Button
                className={styles.ask}
                variant="danger"
                buttonStyle="inverted"
                size="xsmall"
                onClick={(event) => {
                    /* The toast closes on its own click; the button decides that itself. */
                    event.stopPropagation();
                    ask();
                }}
            >
                Ask the assistant
            </Button>
        </div>
    );
};

let installed = false;

/* What react-toastify itself agrees to render. It drops anything else -- callers pass an Error, or
   `err.response?.data?.text` when there is no response -- so wrapping such content would turn a
   toast that never showed into an empty one, or into an Error rendered as a React child, which
   throws outside every error boundary and blanks the whole app. */
const renderable = (content) =>
    typeof content === "string" ||
    typeof content === "number" ||
    typeof content === "function" ||
    React.isValidElement(content);

/**
 * Wraps `toast.error` once, so the 246 places that call it need not change: a toast shown just
 * after a request failed, or after the page itself threw, gets the button; any other toast — a
 * form's own validation message — goes out untouched. So does every toast while no assistant
 * window is listening: a user without CRM_ASSISTANT sees exactly what they saw
 * before.
 */
export const installErrorToastButton = () => {
    if (installed) return;
    installed = true;
    const original = toast.error;
    toast.error = (content, options) => {
        if (!renderable(content) || !canAskAboutError()) return original(content, options);
        const failure = claimRecentFailure();
        if (!failure) return original(content, options);
        return original(<ErrorToast content={content} failure={failure} />, {
            autoClose: AUTO_CLOSE_WITH_BUTTON_MS,
            ...options,
        });
    };
};
